import React, { useState, useEffect } from 'react';
import { Phone, History, Voicemail as VoicemailIcon, User, Settings, X, Mic, MicOff } from 'lucide-react';
import { PhoneDialer } from './PhoneDialer';
import { CallHistory } from './CallHistory';
import { VoicemailPlayerEnhanced } from './VoicemailPlayerEnhanced';
import { config } from '@/src/config';
import { useFormattedDate } from '../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

interface Provider {
  provider_id?: string;
  id?: string;
  primary_phone?: string;
  phone?: string;
  phone_number?: string;
  name?: string;
}

interface Referral {
  id: string;
  first_name: string;
  last_name: string;
}

interface PhoneInterfaceProps {
  provider: Provider;
  referrals: Referral[];
}

type TabType = 'dialer' | 'contacts' | 'history' | 'voicemail';

export const PhoneInterface: React.FC<PhoneInterfaceProps> = ({ provider, referrals }) => {
  const { fmt } = useFormattedDate();
  const [activeTab, setActiveTab] = useState<TabType>('dialer');
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [calling, setCalling] = useState(false);
  const [callSid, setCallSid] = useState<string | null>(null);
  const [callStatus, setCallStatus] = useState<string>('');

  // Collect provider phone numbers
  const providerContacts = [];
  if (provider.primary_phone) {
    providerContacts.push({
      label: 'Main Phone',
      number: provider.primary_phone,
      type: 'work'
    });
  }
  if (provider.phone && provider.phone !== provider.primary_phone) {
    providerContacts.push({
      label: 'Mobile',
      number: provider.phone,
      type: 'mobile'
    });
  }
  if (provider.phone_number &&
      provider.phone_number !== provider.primary_phone &&
      provider.phone_number !== provider.phone) {
    providerContacts.push({
      label: 'Alternative',
      number: provider.phone_number,
      type: 'other'
    });
  }

  const initiateCall = async (phoneNumber: string) => {
    try {
      setCalling(true);
      setCallStatus('initiating');

      const response = await authFetch(`${config.weaveinApi.baseUrl}/api/providers/${provider.provider_id || provider.id}/communications/phone`, {
        method: 'POST',
        jsonBody: {
          to_number: phoneNumber,
          referral_id: selectedReferral?.id,
          provider_id: provider.provider_id || provider.id,
          call_type: selectedReferral ? 'referral' : 'general',
          context_message: selectedReferral
            ? `Referral for ${selectedReferral.first_name} ${selectedReferral.last_name}`
            : 'General inquiry',
          record: recordingEnabled
        }
      });

      const result = await response.json();

      if (result.success) {
        setCallSid(result.call_sid);
        setCallStatus('ringing');
        // Switch to history tab to show active call
        setTimeout(() => setActiveTab('history'), 500);
      } else {
        throw new Error(result.error || 'Call failed');
      }

    } catch (error) {
      console.error('Call error:', error);
      setCallStatus('failed');
      alert(`Failed to initiate call: ${error.message}`);
    } finally {
      setCalling(false);
    }
  };

  const endCall = () => {
    setCallSid(null);
    setCallStatus('');
  };

  const formatPhoneNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, '');
    const match = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return number;
  };

  const tabs = [
    { id: 'dialer' as TabType, icon: Phone, label: 'Dialer' },
    { id: 'contacts' as TabType, icon: User, label: 'Contacts' },
    { id: 'history' as TabType, icon: History, label: 'Recent' },
    { id: 'voicemail' as TabType, icon: VoicemailIcon, label: 'Voicemail' },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Phone App Container */}
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-8 border-gray-900">
        {/* Status Bar */}
        <div className="bg-gray-900 text-white px-6 py-2 flex items-center justify-between text-xs">
          <span className="font-medium">Trust Marketplace</span>
          <div className="flex items-center gap-3">
            <span>{fmt.time(new Date())}</span>
          </div>
        </div>

        {/* Active Call Banner */}
        {callSid && (
          <div className="bg-green-600 text-white px-4 py-3 flex items-center justify-between animate-pulse">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5" />
              <div>
                <p className="font-semibold">{callStatus}</p>
                <p className="text-sm opacity-90">Call ID: {callSid.substring(0, 12)}...</p>
              </div>
            </div>
            <button
              onClick={endCall}
              className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-full font-semibold"
            >
              End
            </button>
          </div>
        )}

        {/* Settings Bar */}
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Call Settings</span>
            </div>
            <button
              onClick={() => setRecordingEnabled(!recordingEnabled)}
              className={`
                flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium
                transition-all ${
                  recordingEnabled
                    ? 'bg-red-100 text-red-700 border-2 border-red-300'
                    : 'bg-gray-200 text-gray-600 border-2 border-gray-300'
                }
              `}
            >
              {recordingEnabled ? (
                <>
                  <Mic className="w-4 h-4" />
                  Recording ON
                </>
              ) : (
                <>
                  <MicOff className="w-4 h-4" />
                  Recording OFF
                </>
              )}
            </button>
          </div>

          {/* Referral Selector */}
          {referrals.length > 0 && (
            <div className="mt-3">
              <select
                value={selectedReferral?.id || ''}
                onChange={(e) => {
                  const referral = referrals.find(r => r.id === e.target.value);
                  setSelectedReferral(referral || null);
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
              >
                <option value="">No referral linked</option>
                {referrals.map((ref) => (
                  <option key={ref.id} value={ref.id}>
                    📋 Referral: {ref.first_name} {ref.last_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="bg-white min-h-[600px] p-4">
          {activeTab === 'dialer' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 text-center mb-4">
                Dial any number
              </h2>
              <PhoneDialer
                onCall={initiateCall}
                initialNumber=""
              />
              <div className="text-center text-sm text-gray-500 mt-4">
                {recordingEnabled ? '🔴 Calls will be recorded' : '⚪ Recording disabled'}
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="space-y-3">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Provider Contacts
              </h2>

              {providerContacts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No saved contacts</p>
                  <p className="text-sm mt-1">Use the dialer to call any number</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {providerContacts.map((contact, idx) => (
                    <button
                      key={idx}
                      onClick={() => initiateCall(contact.number)}
                      disabled={calling}
                      className="
                        w-full p-4 bg-white border-2 border-gray-200 rounded-xl
                        hover:border-blue-400 hover:bg-blue-50 transition-all
                        disabled:opacity-50 disabled:cursor-not-allowed
                        flex items-center justify-between group
                      "
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Phone className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900">{contact.label}</p>
                          <p className="text-sm text-gray-600">
                            {formatPhoneNumber(contact.number)}
                          </p>
                          <p className="text-xs text-gray-500 capitalize">{contact.type}</p>
                        </div>
                      </div>
                      <div className="
                        w-10 h-10 bg-green-600 rounded-full flex items-center justify-center
                        group-hover:scale-110 transition-transform
                      ">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>To call a different number, use the <strong>Dialer</strong> tab</span>
                </p>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <CallHistory
              referralId={selectedReferral?.id}
              providerId={provider.provider_id || provider.id}
            />
          )}

          {activeTab === 'voicemail' && (
            <VoicemailPlayerEnhanced
              referralId={selectedReferral?.id}
            />
          )}
        </div>

        {/* Bottom Navigation */}
        <div className="bg-white border-t-2 border-gray-200 px-2 py-3 flex items-center justify-around">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex flex-col items-center gap-1 px-4 py-2 rounded-lg
                transition-all ${
                  activeTab === tab.id
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-100'
                }
              `}
            >
              <tab.icon className={`w-6 h-6 ${activeTab === tab.id ? 'scale-110' : ''}`} />
              <span className="text-xs font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Helper Text */}
      <div className="mt-4 text-center text-sm text-gray-600">
        <p>
          {recordingEnabled
            ? '🔴 Call recording is enabled - All calls will be recorded'
            : '⚪ Call recording is disabled - Calls will not be recorded'
          }
        </p>
      </div>
    </div>
  );
};
